import * as client from 'openid-client';
import type { AppCredentials, OAuthToken } from '@/types';

// Mastodon には、 PKCE に対応しているバージョンと対応していないバージョンがあると思われる

class OAuthService {
  /**
   * アプリケーションのクレデンシャルを取得または作成
   * @param domain - Mastodonインスタンスのドメイン
   * @returns アプリケーションのクレデンシャル
   */
  async getAppCredentials(domain: string): Promise<AppCredentials> {
    const storedCredentialsStr = localStorage.getItem(`app_credentials_${domain}`);
    if (storedCredentialsStr) {
      const parsedCredentials = JSON.parse(storedCredentialsStr);
      return parsedCredentials;
    }

    // アプリケーション登録
    const response = await fetch(`https://${domain}/api/v1/apps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_name: "Mastodon LTL遡りツール",
        redirect_uris: location.origin + location.pathname,
        scopes: "read:statuses",
        website: location.origin + location.pathname
      })
    });

    if (!response.ok) {
      throw new Error(`アプリケーション登録に失敗しました: ${response.status}`);
    }

    const credentials = await response.json();
    const c2 = {
      client_id: credentials.client_id,
      client_secret: credentials.client_secret,
    }
    localStorage.setItem(`app_credentials_${domain}`, JSON.stringify(c2));
    return c2;
  }

  private getConfig(domain: string, clientId: string, clientSecret: string): client.Configuration {
    return new client.Configuration(
      {
        issuer: `https://${domain}`,
        authorization_endpoint: `https://${domain}/oauth/authorize`,
        token_endpoint: `https://${domain}/oauth/token`,
      },
      clientId,
      undefined,
      client.ClientSecretPost(clientSecret)
    );
  }

  /**
   * 認可URLを生成
   * @param domain - Mastodonインスタンスのドメイン
   * @param clientId - クライアントID
   * @param clientSecret - クライアントシークレット
   * @returns 認可URL
   */
  async buildAuthUrl(domain: string, clientId: string, clientSecret: string): Promise<{ url: string, state: string, verifier: string }> {
    const config = this.getConfig(domain, clientId, clientSecret);
    const state = client.randomState();
    const verifier = client.randomPKCECodeVerifier();
    const challenge = await client.calculatePKCECodeChallenge(verifier);

    const parameters = {
      redirect_uri: location.origin + location.pathname,
      scope: 'read:statuses',
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    };

    const url = client.buildAuthorizationUrl(config, parameters).href;

    return { url, state, verifier };
  }

  /**
   * アクセストークンを取得
   * @param domain - Mastodonインスタンスのドメイン
   * @param clientId - クライアントID
   * @param clientSecret - クライアントシークレット
   * @param expectedState - 期待されるstate値
   * @param verifier - PKCE verifier
   * @returns アクセストークン情報
   */
  async getAccessToken(
    domain: string,
    clientId: string,
    clientSecret: string,
    expectedState: string,
    verifier: string
  ): Promise<OAuthToken> {
    try {
      const config = this.getConfig(domain, clientId, clientSecret);
      const tokens = await client.authorizationCodeGrant(
        config,
        new URL(location.href),
        {
          pkceCodeVerifier: verifier,
          expectedState: expectedState,
        },
      );

      return {
        access_token: tokens.access_token
      };
    } catch (error) {
      // TODO
      throw error;
    }
  }
}

export const oAuthService = new OAuthService();
